import React, { useState, useEffect, useCallback } from 'react';
import { Table, Row, Col, Container, Form, InputGroup, Badge, Pagination, OverlayTrigger, Tooltip, Alert } from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';
import '../../../custom.css';
import { getCurrencySymbol } from '../common_components/choices';
import CouponDetails from './CouponDetails';
import FloatingActionButton from '../common_components/FloatingActionButton';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import _ from 'lodash';
import { getPaginationItems } from '../common_components/getPaginationItems';

const CouponsList = () => {
    const token = localStorage.getItem('token');
    const [coupons, setCoupons] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState(''); // New state for debounced search text

    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState({ value: 'active', label: 'Active' }); // Default selected status is 'active'

    // Removed unused states: addCoupon, startDate, endDate

    // States for pagination
    const pageSize = 8;
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // State for the coupon details modal
    const [showCouponModal, setShowCouponModal] = useState(false);
    const [selectedCoupon, setSelectedCoupon] = useState(null);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchText(searchText); // Update debounced search text after delay
        }, 300);

        return () => {
            clearTimeout(handler); // Clear timeout if user types again within delay
        };
    }, [searchText]);

    const fetchCoupons = useCallback(async (searchTerm, category, status, page = 1) => {
        try {
            const params = {
                is_owner: true, // Only fetch coupons created by the user
                page_size: pageSize,
                page,
                search: searchTerm || undefined,
                category_slug: category ? category.value : undefined,
                status: status ? status.value : undefined, // Use status value
            };

            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/astapor/coupons/`, {
                params,
                headers: {
                    Authorization: `Token ${token}`,
                }
            });

            setTotalPages(Math.ceil(response.data.count / pageSize));

            const sortedCoupons = response.data.results.sort((a, b) => {
                return new Date(a.valid_to) - new Date(b.valid_to);
            });
            setCoupons(sortedCoupons);
        } catch (error) {
            console.error('Error fetching coupons:', error);
            toast.error('Error fetching coupons');
        }
    }, [token]);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/astapor/coupon-categories/`, {
                headers: { Authorization: `Token ${token}` }
            });
            const categoriesData = response.data.results.map(category => ({
                label: category.name,
                value: category.id
            }));
            setCategories(categoriesData);
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Error fetching categories');
        }
    }, [token]);

    useEffect(() => {
        fetchCoupons(debouncedSearchText, selectedCategory, selectedStatus, currentPage);
        fetchCategories();
        document.title = 'Saarthi - Coupons';
    }, [fetchCoupons, fetchCategories, debouncedSearchText, selectedCategory, selectedStatus, currentPage]);

    const handleShowCouponModal = (coupon) => {
        setSelectedCoupon(coupon);
        setShowCouponModal(true);
    };

    const handleCloseCouponModal = () => {
        setSelectedCoupon(null);
        setShowCouponModal(false);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber); // Explicitly fetch data for the new page
    };

    const loadCategoryOptions = _.debounce(async (inputValue, callback) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/astapor/coupon-categories/`, {
                params: { search: inputValue, page_size: 10 },
                headers: { Authorization: `Token ${token}` },
            });

            const categories = response.data.results.map(category => ({
                label: category.name,
                value: category.slug
            }));
            callback(categories);
        } catch (error) {
            console.error('Error loading category options:', error);
            callback([]);
        }
    }, 300);

    const statusOptions = [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'used', label: 'Used' }
    ];

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <Badge bg="success">Active</Badge>;
            case 'inactive':
                return <Badge bg="secondary">Inactive</Badge>;
            case 'used':
                return <Badge bg="warning">Used</Badge>;
            default:
                return <Badge bg="light">Unknown</Badge>;
        }
    };

    const renderDiscount = (coupon) => {
        if (coupon.coupon_discount) {
            return `${coupon.coupon_discount}%`; // Show percentage discount
        } else if (coupon.coupon_amount) {
            const currencySymbol = getCurrencySymbol(coupon.currency || 'USD');
            if (coupon.minimum_amount) {
                return (
                    <div className="d-flex justify-content-between align-items-center">
                        <span>{`${currencySymbol}${coupon.coupon_amount}`}</span>
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id={`tooltip-minimum-${coupon.id}`}>Minimum purchase amount to avail coupon discount.</Tooltip>}
                        >
                            <Badge bg="danger" className="ml-2">
                                {`Min: ${currencySymbol}${coupon.minimum_amount}`}
                            </Badge>
                        </OverlayTrigger>
                    </div>
                );
            } else {
                return `${currencySymbol}${coupon.coupon_amount}`;
            }
        } else {
            return 'N/A'; // No discount available
        }
    };


    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Coupon code copied to clipboard');
    };

    // Render the coupon code with the copy icon
    const renderCouponCode = (coupon) => {
        return (
            <div className="d-flex align-items-center">
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-copy-${coupon.id}`}>Copy</Tooltip>}
                >
                    <i
                        className="bi bi-clipboard"
                        style={{ cursor: 'pointer', marginRight: '8px' }} // Inline style for margin-right
                        onClick={(e) => {
                            copyToClipboard(coupon.code);
                            e.stopPropagation();
                        }}
                    ></i>
                </OverlayTrigger>
                <span>{coupon.code}</span>
            </div>
        );
    };




    return (
        <Container style={{ display: 'flex', flexDirection: 'column', height: '100vh', paddingBottom: '20px' }}>
            <Row>
                <Col md={4}>
                    <InputGroup>
                        <Form.Control
                            type="text"
                            placeholder="Search..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </InputGroup>
                </Col>
                <Col md={4}>
                    <AsyncSelect
                        cacheOptions
                        loadOptions={loadCategoryOptions}
                        defaultOptions
                        placeholder="Filter by category..."
                        value={selectedCategory}
                        onChange={setSelectedCategory}
                        isClearable
                        menuPlacement="auto"  // This ensures the dropdown doesn't get cut off
                        styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}  // Ensures dropdown is above other elements
                    />
                </Col>
                <Col md={4}>
                    {/* Dropwdown for the status filter */}
                    <Select
                        className="status-select"
                        placeholder="Filter by status..."
                        options={statusOptions}
                        value={selectedStatus}
                        onChange={setSelectedStatus}
                        isClearable={true}
                        styles={{ menu: base => ({ ...base, zIndex: 9999 }) }} // Ensure dropdown appears above other elements
                    />
                </Col>
            </Row>
            <Row className="mt-3" style={{ flex: 1 }}>
                {coupons.length > 0 ? (
                    <Col className="table-responsive">
                        <Table striped hover>
                            <thead>
                                <tr>
                                    <th>Provider</th>
                                    <th>Coupon code</th>
                                    <th>Discount</th>
                                    <th>End date</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {coupons.map(coupon => (
                                    <tr key={coupon.id} onClick={() => handleShowCouponModal(coupon)} style={{ cursor: 'pointer' }}>
                                        <td>{coupon.provider}</td>
                                        <td>{renderCouponCode(coupon)}</td>
                                        <td>{renderDiscount(coupon)}</td>
                                        <td>{moment(coupon.valid_to).format('DD-MMM-YYYY')}</td>
                                        <td>{getStatusBadge(coupon.status)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Col>
                ) : (
                    <Col>
                        <Alert variant="warning">No coupons found.</Alert>
                    </Col>
                )}
            </Row>
            <Row>
                <Col>
                    <Pagination>
                        <Pagination.Prev
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        />
                        {getPaginationItems(currentPage, totalPages, 11, handlePageChange)}
                        <Pagination.Next
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        />
                    </Pagination>
                </Col>
            </Row>
            <CouponDetails
                show={showCouponModal}
                onHide={handleCloseCouponModal}
                refreshCoupons={fetchCoupons}
                coupon={selectedCoupon}
                categories={categories}
            />
            <FloatingActionButton
                actions={[
                    {
                        icon: 'bi bi-plus-square',
                        handler: () => handleShowCouponModal(null),
                        color: 'primary',
                        tooltip: 'Add coupon'
                    }
                ]}
            />
        </Container>
    );
};

export default CouponsList;
